<template>
  <div>
    <b-row>
      <b-col class="text-center">
        <h2>Two-Factor Authentication</h2>
      </b-col>
    </b-row>
    <b-form class="mb-3" v-if="isConfigurationStep">
      <b-row>
        <b-col>
          <div class="mb-4 t-small-text text-muted text-center">
            You're required to enable two-factor authentication - follow these
            steps to get setup now.
          </div>
          <div class="mb-3 t-small-text text-muted">
            <span class="font-weight-bold">Step 1:</span> Download an
            authenticator app on your mobile device.
          </div>
          <div class="mb-4">
            <div class="mb-2 t-small-text text-muted">
              <span class="font-weight-bold">Step 2:</span> To register your
              device, scan this QR code, or manually enter the code below:
            </div>
            <div class="mb-2 text-center">
              <img
                :src="twoFactorAuthForm.qrCode"
                class="qr-code border border-dark"
              />
            </div>
          </div>
          <div class="mb-4">
            <b-form-group
              id="type"
              label-for="otp-secret"
              label=""
              class="mb-2"
            >
              <b-form-input
                data-test-id="profile-security-otp-secret"
                id="otp-secret"
                v-model="otpSecret"
                za
                readonly
                size="sm"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="mb-2 t-small-text text-muted">
            <span class="font-weight-bold">Step 3:</span> Enter the six-digit
            code generated by your authenticator app:
          </div>
          <b-form-group
            class="mb-5"
            label-for="code"
            label=""
            :state="$validateState($v.twoFactorAuthForm.code)"
          >
            <b-form-input
              size="sm"
              id="code"
              data-test-id="profile-security-2fa-code"
              v-model="$v.twoFactorAuthForm.code.$model"
              v-on:keyup.enter="on2FASubmit"
              :state="$validateState($v.twoFactorAuthForm.code)"
              placeholder="For example, 123456"
              :disabled="fetching"
              @keyup="cleanErrorMessage"
            />
            <template #invalid-feedback>
              <p v-if="!$v.twoFactorAuthForm.code.required">
                {{ $t("This is a required field.") }}
              </p>
            </template>
            <p v-if="errorMessage" class="text-danger">
              {{ errorMessage }}
            </p>
          </b-form-group>
          <b-button
            class="btn-block"
            @click="on2FASubmit"
            :disabled="fetching || Boolean(errorMessage)"
          >
            Register
            <span class="px-2" v-if="fetching">
              <b-icon icon="arrow-clockwise" animation="spin"
            /></span>
          </b-button>
          <b-button
            class="btn-block"
            variant="outline-secondary"
            :disabled="fetching"
            @click="onCancelClick"
            >Cancel</b-button
          >
        </b-col>
      </b-row>
    </b-form>
    <b-form class="mb-2 text-center" v-else>
      <b-row>
        <b-col>
          <div class="mb-3">Save your recovery codes now!</div>
        </b-col>
      </b-row>
      <b-row v-if="this.backupCodes !== null">
        <b-col>
          <div class="mb-2 font-weight-bold">Recovery Codes</div>
          <div class="mb-3 t-small-text text-muted">
            These recovery codes are only shown once, so copy and save them now.
            You'll need them to log in if you lose your device or can't access
            your authenticator app.
          </div>
          <div class="mb-3">
            <b-card>
              <div class="mb-3">
                <span v-for="(code, idx) in this.backupCodes" :key="idx">
                  {{ code }},
                </span>
              </div>
              <div class="d-flex justify-content-end">
                <b-button
                  class="btn-tertiary align-self-end"
                  @click="copyTwoFACodes"
                >
                  <div class="d-flex align-items-center">
                    <b-icon icon="front" class="mr-2"></b-icon><span>Copy</span>
                  </div>
                </b-button>
              </div>
            </b-card>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div>
            <b-button @click="onLoginClick" class="btn-block">Login</b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>
<script>
import { required } from "vuelidate/lib/validators";
import { authenticator } from "otplib";
import qrcode from "qrcode";
import { mapActions, mapGetters } from "vuex";

export default {
  props: {
    email: {
      type: String,
      required,
    },
    password: {
      type: String,
      required,
    },
  },
  data() {
    return {
      isConfigurationStep: true,
      twoFactorAuthForm: {
        code: null,
      },
      otpSecret: "",
      fetching: false,
      errorMessage: null,
    };
  },
  validations: {
    twoFactorAuthForm: {
      code: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters("authentication", ["backupCodes"]),
  },
  methods: {
    ...mapActions("authentication", ["requestToken"]),
    ...mapActions("toast", ["displayToast"]),
    generateQRCode() {
      this.otpSecret = authenticator.generateSecret(20);
      const otpauth = authenticator.keyuri(this.email, "WTC", this.otpSecret);

      qrcode.toDataURL(otpauth, (err, imageUrl) => {
        if (err) {
          console.error("Error with QR"); // eslint-disable-line no-console
          return;
        }
        this.twoFactorAuthForm.qrCode = imageUrl;
      });
    },
    async on2FASubmit() {
      debugger;
      this.$v.twoFactorAuthForm.$touch();

      if (this.$v.twoFactorAuthForm.$invalid) {
        return;
      }
      this.fetching = true;
      try {
        await this.requestToken({
          username: this.email,
          password: this.password,
          secret: this.otpSecret,
          code: this.twoFactorAuthForm.code,
        });
        this.$emit("success");
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
        this.isConfigurationStep = false;
      } catch (err) {
        if (err.response.status === 422) {
          this.errorMessage = "Incorrect code entered.";
          return;
        }
        if (err.response.status === 400) {
          this.errorMessage = "Please enter a 6 digit code.";
          return;
        }
      } finally {
        this.fetching = false;
      }
    },
    async copyTwoFACodes() {
      await navigator.clipboard.writeText(this.backupCodes);
      this.displayToast({
        title: this.$t("Copied"),
        message: this.$t("Copied to clipboard."),
        duration: 2000,
        variant: "info",
      });
    },
    onCancelClick() {
      this.$emit("cancel");
    },
    onLoginClick() {
      this.$emit("login");
    },
    cleanErrorMessage() {
      this.errorMessage = null;
    },
  },
  created() {
    if (this.isConfigurationStep) {
      this.generateQRCode();
    }
  },
};
</script>
<style scoped>
.qr-code {
  width: 150px;
}
</style>
